import React from 'react';
import { Button, Row, InputGroup, InputGroupAddon, Input } from 'reactstrap';
import { Colxx } from '../../../components/common/CustomBootstrap';
import classnames from 'classnames';
import { AddCommaNumber } from '../../../helpers/Utils';
import IntlMessages from '../../../helpers/IntlMessages';
import ProductDetailNow from '../../dashboards/ProductDetailNow';
import {
    getVariantQuantity,
    isVariantSelectionComplete,
} from '../../../helpers/ProductOptionDefaults';
import {
    sortOptionsByPriceAsc,
    sortSizeOptionsByPriceAsc,
} from '../../../helpers/ProductOptionSort';
import { NotificationManager } from '../../../components/common/react-notifications';

const ContentComponent = ({
    product,
    onSelected,
    order,
    detailDisplay,
    onToggleDetail,
    isMobileDetail = false,
    onCloseDetail,
    onAddToCartWithQuantity,
}) => {
    const clsName =
        order && order.count
            ? 'input-order order-number-active'
            : 'input-order';
    const [selectedQuantity, setSelectedQuantity] = React.useState(1);
    const [showErrors, setShowErrors] = React.useState(false);
    const selectedVariantQuantity =
        product.selectedVariantQuantity !== undefined
            ? product.selectedVariantQuantity
            : getVariantQuantity(product.selectedVariant);
    const hasSelectedVariantQuantity =
        selectedVariantQuantity !== null && selectedVariantQuantity !== undefined;
    const isSelectedVariantOutOfStock =
        hasSelectedVariantQuantity && selectedVariantQuantity <= 0;

    React.useEffect(() => {
        if (detailDisplay) {
            setSelectedQuantity(1);
        }
        setShowErrors(false);
    }, [detailDisplay, product.id]);

    React.useEffect(() => {
        if (!hasSelectedVariantQuantity || selectedVariantQuantity <= 0) return;

        setSelectedQuantity((prev) =>
            prev > selectedVariantQuantity ? selectedVariantQuantity : prev,
        );
    }, [hasSelectedVariantQuantity, selectedVariantQuantity]);

    const validateAddToCart = (quantityToAdd) => {
        let hasError = false;
        if (product.variants?.length && !isVariantSelectionComplete(product)) {
            NotificationManager.warning("Vui lòng chọn đầy đủ thuộc tính biến thể trước khi thêm vào giỏ hàng");
            hasError = true;
        }
        if (!product.variants?.length && Array.isArray(product.size) && product.size.length && (!product.sizeSelectedId || product.sizeSelectedId.length === 0)) {
            NotificationManager.warning("Vui lòng chọn kích thước trước khi gọi món");
            hasError = true;
        }
        if (!product.variants?.length && Array.isArray(product.flavor) && product.flavor.length && (!product.flavorSelectedId || product.flavorSelectedId.length === 0)) {
            NotificationManager.warning("Vui lòng chọn vị trước khi gọi món");
            hasError = true;
        }
        if (!hasError && isSelectedVariantOutOfStock) {
            NotificationManager.warning("Biến thể này đã hết hàng");
            hasError = true;
        }
        if (!hasError && hasSelectedVariantQuantity && quantityToAdd > selectedVariantQuantity) {
            NotificationManager.warning(`Số lượng tồn kho chỉ còn ${AddCommaNumber(selectedVariantQuantity)}`);
            hasError = true;
        }
        if (hasError) {
            setShowErrors(true);
            return false;
        }

        return true;
    };

    const handleAddToCart = () => {
        const quantityToAdd = selectedQuantity > 0 ? selectedQuantity : 1;
        if (!validateAddToCart(quantityToAdd)) {
            return false;
        }

        return onToggleDetail({ product, name: 'orderDisplay' });
    };

    const handleAddToCartWithQuantity = () => {
        const quantityToAdd = selectedQuantity > 0 ? selectedQuantity : 1;

        if (!validateAddToCart(quantityToAdd)) {
            return;
        }

        if (onAddToCartWithQuantity) {
            const added = onAddToCartWithQuantity({
                product,
                quantity: quantityToAdd,
            });
            if (added === false) {
                return;
            }
        } else {
            const added = handleAddToCart();
            if (added === false) {
                return;
            }
        }

        if (onCloseDetail) {
            onCloseDetail();
        }
    };

    const handleQuantityChange = (e) => {
        const nextValue = parseInt(e.target.value, 10);
        if (Number.isNaN(nextValue)) {
            setSelectedQuantity(1);
            return;
        }
        const normalizedValue = nextValue < 1 ? 1 : nextValue;
        if (hasSelectedVariantQuantity && selectedVariantQuantity > 0) {
            setSelectedQuantity(
                normalizedValue > selectedVariantQuantity
                    ? selectedVariantQuantity
                    : normalizedValue,
            );
            return;
        }
        setSelectedQuantity(normalizedValue);
    };

    const renderAddToCartActions = () => (
        <div
            className={classnames('product-detail-add-actions', {
                'product-detail-mobile-actions': isMobileDetail,
            })}
        >
            <InputGroup size='md' className='mb-0 product-detail-quantity-group'>
                <InputGroupAddon addonType='prepend'>
                    <Button
                        outline
                        color='primary'
                        onClick={() =>
                            setSelectedQuantity((prev) =>
                                prev - 1 > 0 ? prev - 1 : 1,
                            )
                        }
                        className='border-btn-left'
                    >
                        {'-'}
                    </Button>
                </InputGroupAddon>
                <Input
                    type='number'
                    min='1'
                    max={hasSelectedVariantQuantity && selectedVariantQuantity > 0 ? selectedVariantQuantity : undefined}
                    disabled={isSelectedVariantOutOfStock}
                    className={clsName}
                    value={selectedQuantity}
                    onChange={handleQuantityChange}
                />
                <InputGroupAddon addonType='append'>
                    <Button
                        outline
                        color='primary'
                        onClick={() =>
                            setSelectedQuantity((prev) => {
                                const nextQuantity = prev + 1;
                                if (
                                    hasSelectedVariantQuantity &&
                                    selectedVariantQuantity > 0
                                ) {
                                    return nextQuantity > selectedVariantQuantity
                                        ? selectedVariantQuantity
                                        : nextQuantity;
                                }
                                return nextQuantity;
                            })
                        }
                        disabled={
                            isSelectedVariantOutOfStock ||
                            (hasSelectedVariantQuantity &&
                                selectedQuantity >= selectedVariantQuantity)
                        }
                        className='border-btn-right'
                    >
                        {'+'}
                    </Button>
                </InputGroupAddon>
            </InputGroup>
            <Button
                color='primary'
                onClick={handleAddToCartWithQuantity}
                disabled={isSelectedVariantOutOfStock}
                className={classnames('product-detail-add-btn', {
                    'product-detail-mobile-done-btn': isMobileDetail,
                })}
            >
                {isSelectedVariantOutOfStock ? 'Hết hàng' : 'Thêm vào giỏ hàng'}
            </Button>
        </div>
    );

    return (
        <div
            className={classnames('product-btn w-100', {
                'product-detail-mobile-sheet': isMobileDetail,
            })}
        >
            {product.variants?.length ? (
                <ProductDetailNow
                    product={product}
                    variantOptionsOnly
                    onSelected={(updatedProduct) =>
                        onSelected({
                            product,
                            name: 'variantSelection',
                            value: updatedProduct,
                        })
                    }
                    showErrors={showErrors}
                />
            ) : null}
            {!product.variants?.length && product.size && Array.isArray(product.size) && product.size.length ? (
                (() => {
                    const isMissingSize = showErrors && (!product.sizeSelectedId || product.sizeSelectedId.length === 0);
                    return (
                        <div
                            className='mb-3'
                            style={isMissingSize ? {
                                border: '2px dashed #ffc107',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 193, 7, 0.03)',
                            } : null}
                        >
                            <div className="mb-2 text-muted">
                                {product.sizeTitle ? (
                                    <span>{product.sizeTitle}</span>
                                ) : (
                                    <IntlMessages id='product.size' />
                                )} <span className='text-danger'>* (Bắt buộc)</span>
                            </div>
                            <SizeAndToppingComponent
                                data={product.size}
                                onSelected={onSelected}
                                selectedId={product.sizeSelectedId || []}
                                name={'sizeSelectedId'}
                                product={product}
                            />
                            {isMissingSize && (
                                <div className="text-danger small mt-1 font-weight-bold">
                                    Vui lòng chọn kích thước
                                </div>
                            )}
                        </div>
                    );
                })()
            ) : null}
            {!product.variants?.length && product.flavor && Array.isArray(product.flavor) && product.flavor.length ? (
                (() => {
                    const isMissingFlavor = showErrors && (!product.flavorSelectedId || product.flavorSelectedId.length === 0);
                    return (
                        <div
                            className='mb-3'
                            style={isMissingFlavor ? {
                                border: '2px dashed #ffc107',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 193, 7, 0.03)',
                            } : null}
                        >
                            <div className="mb-2 text-muted">
                                <span>Vị <span className='text-danger'>* (Bắt buộc)</span></span>
                            </div>
                            <SizeAndToppingComponent
                                data={product.flavor || []}
                                name={'flavorSelectedId'}
                                product={product}
                                selectedId={product.flavorSelectedId || []}
                                onSelected={onSelected}
                            />
                            {isMissingFlavor && (
                                <div className="text-danger small mt-1 font-weight-bold">
                                    Vui lòng chọn vị
                                </div>
                            )}
                        </div>
                    );
                })()
            ) : null}
            {product.topping && product.topping.length ? (
                <div className='mb-3'>
                    <div className="mb-2 text-muted">
                        {product.toppingTitle ? (
                            <span>{product.toppingTitle}</span>
                        ) : (
                            <IntlMessages id='product.topping' />
                        )} <span className="text-muted small">(Không bắt buộc)</span>
                    </div>
                    <SizeAndToppingComponent
                        data={product.topping}
                        name={'toppingSelectedId'}
                        product={product}
                        selectedId={product.toppingSelectedId || []}
                        onSelected={onSelected}
                    />
                </div>
            ) : null}
            {product.ice && product.ice.length ? (
                <div className='mb-3'>
                    <div className="mb-2 text-muted">
                        {product.iceTitle ? (
                            <span>{product.iceTitle}</span>
                        ) : (
                            <IntlMessages id='product.ice' />
                        )} <span className="text-muted small">(Không bắt buộc)</span>
                    </div>
                    <IceAndSugarComponent
                        data={product.ice}
                        name={'iceSelectedValue'}
                        product={product}
                        selectedValue={product.iceSelectedValue}
                        onSelected={onSelected}
                    />
                </div>
            ) : null}
            {product.sugar && product.sugar.length ? (
                <div className='mb-3'>
                    <div className="mb-2 text-muted">
                        {product.sugarTitle ? (
                            <span>{product.sugarTitle}</span>
                        ) : (
                            <IntlMessages id='product.sugar' />
                        )} <span className="text-muted small">(Không bắt buộc)</span>
                    </div>
                    <IceAndSugarComponent
                        data={product.sugar}
                        name={'sugarSelectedValue'}
                        product={product}
                        selectedValue={product.sugarSelectedValue}
                        onSelected={onSelected}
                    />
                </div>
            ) : null}
            <Row className='mb-4 mt-3'>
                <Colxx className='col-12 p-0'>
                    <InputGroup size='sm'>
                        <Input
                            onChange={(e) =>
                                onToggleDetail({
                                    product,
                                    name: 'note',
                                    value: e.target.value,
                                })
                            }
                            className='custom-input-underline'
                            placeholder='Ghi chú'
                        />
                    </InputGroup>
                </Colxx>
            </Row>

            {!isMobileDetail ? (
                <div className='mb-3 btn-center'>
                    {renderAddToCartActions()}
                </div>
            ) : (
                <div className='product-detail-mobile-footer'>
                    {renderAddToCartActions()}
                </div>
            )}
        </div>
    );
};

const SizeAndToppingComponent = ({
    data,
    name,
    product,
    selectedId,
    onSelected,
}) => {
    return (
        <Row>
            {(name === 'sizeSelectedId'
                ? sortSizeOptionsByPriceAsc(data)
                : sortOptionsByPriceAsc(data)
            ).map((_item, idx) => (
                <Colxx
                    key={`${name}-${_item.id}`}
                    xxs='12'
                    md='12'
                    xl='12'
                    className='mt-1 pad-2px-10px product-btn'
                >
                    <Button
                        size='sm'
                        outline
                        block
                        onClick={() =>
                            onSelected({ product, name, value: _item.id })
                        }
                        className={classnames({
                            'btn-selected': selectedId.includes(_item.id),
                            'btn-disabled': !selectedId.includes(_item.id),
                        })}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                            width: '100%',
                        }}
                    >
                        <span className='left' style={{ float: 'none', marginRight: '8px', flex: 1 }}>{_item.name}</span>
                        <span className='right' style={{ float: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            + {AddCommaNumber(_item.value)} đ
                        </span>
                    </Button>
                </Colxx>
            ))}
        </Row>
    );
};

const IceAndSugarComponent = ({
    data,
    product,
    name,
    selectedValue,
    onSelected,
}) => {
    return (
        <Row className='pad-2px-8px'>
            {sortOptionsByPriceAsc(data).map((_item, idx) => (
                <Colxx key={idx} _id={idx} className='mt-1 pad-2px w-20'>
                    <Button
                        color='secondary'
                        size='sm'
                        outline
                        block
                        onClick={() =>
                            onSelected({ product, name, value: _item.value })
                        }
                        className={classnames({
                            'btn-selected': selectedValue === _item.value,
                            'btn-disabled': selectedValue !== _item.value,
                        })}
                    >
                        {`${_item.name}%`}
                    </Button>
                </Colxx>
            ))}
        </Row>
    );
};

export default ContentComponent;
